import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bankPrefill, linkBankDoc, paymentNote } from '@/shared/utils/reverseChain';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsSales, type GoodsSaleItem } from '@/api/goodsSales';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DocLayout from '@/shared/components/DocLayout';
import QuickCreateLink from '@/shared/components/QuickCreateLink';
import QuickContractModal from '@/shared/components/QuickContractModal';
import QuickContractorModal from '@/shared/components/QuickContractorModal';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import { customerOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { ProjectField } from '@/components/ProjectSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useContractPriceLookup, useMainContractAutoSelect } from '@/hooks/useContractDefaults';
import { usePermissions } from '@/auth/usePermissions';
import CommissionerWarningModal, {
  type CommissionerWarningChoice,
} from '@/components/CommissionerWarningModal';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import NomenclatureAvailabilityPicker from '@/components/NomenclatureAvailabilityPicker';
import QuickNomenclatureModal from '@/shared/components/QuickNomenclatureModal';
import FixedAssetPickerModal from '@/components/FixedAssetPickerModal';
import { assetsRegistry } from '@/api/assetsRegistry';
import DatePopover from '@/components/DatePopover';
import SearchSelect from '@/components/SearchSelect';
import type { GoodsBalanceRow } from '@/api/documents';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import {
  commissionerWarning,
  type CommissionerWarningCheck,
} from '@/api/commissionerWarning';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { contractLabel } from '@/shared/utils/labels';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';
import VatFlagsButton from '@/shared/components/VatFlagsButton';
import {
  DEFAULT_VAT_FLAGS, lineAmounts, orgVatDefaults, recalcRows, type DocVatFlags, vatPayerOn,
} from '@/shared/utils/vatFlags';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  customer_order_id: string;
  allow_overshipment: boolean;
  /** ч.1374: ознаки ПДВ документа як у 1С (умовчання — з картки організації). */
  vat_flags: DocVatFlags;
  advance_offset_mode: 'AUTO' | 'NONE';
  delivery_address: string;
  comment: string;
  project_id: string | null;
  items: GoodsSaleItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  customer_order_id: '',
  allow_overshipment: false,
  vat_flags: { ...DEFAULT_VAT_FLAGS },
  advance_offset_mode: 'AUTO',
  delivery_address: '',
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (
  n: number,
  vatId: string,
  defaultTaxPurposeId: string | null = null,
): GoodsSaleItem => ({
  line_number: n, nomenclature_id: '', quantity: '0', price: '0',
  discount_percent: '0', discount_amount: '0',
  amount_without_vat: '0', vat_rate_id: vatId, vat_amount: '0', total_amount: '0',
  // 2026-06-02: default TAXABLE (за рішенням власника).
  tax_purpose_id: defaultTaxPurposeId,
});

const fmt = (n: number) => n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ч.854: іконка документа для кнопок «створити …» (як у рахунку). */
function DocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function GoodsSaleEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  // П1: /goods-sales/new?basis=<id> — «створити реалізацію» з рахунку/замовлення.
  const basisId = searchParams.get('basis');
  // ч.1352 (обернений ланцюг): /goods-sales/new?tax_invoice=<id> — реалізація З податкової
  // накладної (зареєстрували раніше, документ обліку — потім); створена реалізація стає
  // підставою накладної (link-base), тож «створити ПН» далі покаже «відкрити ПН».
  const pnBasisId = searchParams.get('tax_invoice');
  const linkPn = async (saleId: string) => {
    if (!pnBasisId) return;
    try {
      await taxInvoicesOutgoing.linkBase(pnBasisId, {
        base_document_type: 'doc.goods_sales', base_document_id: saleId });
      qc.invalidateQueries({ queryKey: ['pn-for-sale'] });
    } catch (e) {
      setError(`Реалізацію створено, але привʼязати її до ПН не вдалося: ${extractError(e)}`);
    }
  };
  // ч.1359 (обернений ланцюг з оплати): /goods-sales/new?bank_receipt=<id> — реалізація З
  // надходження банку (оплату зареєстрували раніше): шапка з платежу; після створення
  // непроведене надходження отримує «На підставі» = ця реалізація, проведене — ручний звʼязок.
  const bankReceiptId = searchParams.get('bank_receipt');
  const linkBank = async (saleId: string) => {
    if (!bankReceiptId) return;
    const problem = await linkBankDoc({ kind: 'receipt', bankId: bankReceiptId, createdType: 'doc.goods_sales',
      createdId: saleId, field: 'goods_sale_id' });
    if (problem) setError(problem);
    void qc.invalidateQueries({ queryKey: ['related-docs'] });
  };
  // 0445: /goods-sales/new?asset_id=<fa>&organization_id=<org> — «продаж» з картки ОЗ.
  const assetIdParam = searchParams.get('asset_id');
  const orgIdParam = searchParams.get('organization_id');
  const { has } = usePermissions();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по sales.realization.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('sales.realization.create');
  const buyerList = useQuery({
    queryKey: ['buyers'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  // TD-B-P5.5.3 (2026-06-01): tax_purposes для UI dropdown.
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const docQuery = useQuery({
    queryKey: ['goods-sale', id],
    queryFn: () => goodsSales.get(id!),
    enabled: !isNew,
  });
  // ч.1267: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'goods-sale', (cid) => goodsSales.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.goods_sales', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  // Block 7.6: warning modal state.
  const [commWarning, setCommWarning] = useState<CommissionerWarningCheck | null>(null);
  // 2026-06-02: Picker modal для додавання товару з залишків.
  const [pickerOpen, setPickerOpen] = useState(false);
  // 2026-07-26: вкладки Товари/Послуги (як у рахунку).
  const [linesTab, setLinesTab] = useState<'goods' | 'services' | 'equipment' | 'assets'>('goods');
  // 0445: вкладка «ОЗ» — продаж основних засобів звичайною реалізацією.
  const [faPickerOpen, setFaPickerOpen] = useState(false);
  // ч.700: швидке створення номенклатури з документа (механіка рахунку).
  const [quickNomOpen, setQuickNomOpen] = useState(false);
  const faListQ = useQuery({
    queryKey: ['fa-for-sale', form.organization_id],
    queryFn: () => assetsRegistry.list({ organization_id: form.organization_id, kind: 'FIXED_ASSET', limit: 500 }),
    enabled: !!form.organization_id,
  });
  const faById = useMemo(() => {
    const m = new Map<string, { name: string; inventory_number: string; residual_value: string; account_code: string | null }>();
    for (const a of faListQ.data?.items ?? []) m.set(a.id, a);
    return m;
  }, [faListQ.data]);
  const faLabel = (id?: string | null) => {
    const a = id ? faById.get(id) : undefined;
    return a ? `${a.inventory_number} — ${a.name}` : '—';
  };
  // 2026-07-27: режими таблиці — рядки / рахунки обліку / податковий облік.
  const [viewTab, setViewTab] = useState<'lines' | 'accounts' | 'tax'>('lines');
  // Превʼю РЕАЛЬНИХ рахунків (та сама логіка, що проведення) — показуємо
  // у полях вкладки «Рахунки обліку» як значення за замовчуванням.
  const previewAccountsQ = useQuery({
    queryKey: ['gs-preview-accounts', form.organization_id,
      form.items.map((it) => `${it.nomenclature_id ?? it.fixed_asset_id ?? ''}:${it.warehouse_id ?? ''}`).join('|'),
      form.warehouse_id],
    queryFn: () => goodsSales.previewAccounts({
      organization_id: form.organization_id,
      // || (не ??): порожній рядок '' у warehouse_id → 422 UUID (баг
      // 2026-07-31: вкладка «Рахунки обліку» падала без складу шапки).
      items: form.items.filter((it) => it.nomenclature_id || it.fixed_asset_id).map((it) => ({
        nomenclature_id: it.nomenclature_id || null,
        fixed_asset_id: it.fixed_asset_id || null,
        warehouse_id: it.warehouse_id || form.warehouse_id || null,
      })),
    }),
    enabled: viewTab === 'accounts' && !!form.organization_id
      && form.items.some((it) => it.nomenclature_id || it.fixed_asset_id),
  });
  // Ключ — номенклатура АБО актив (рядок-ОЗ, 0445).
  const previewByNom = useMemo(() => {
    const m = new Map<string, { account_code: string | null; revenue_account_code: string; cost_account_code: string | null }>();
    for (const r of previewAccountsQ.data ?? []) m.set(r.nomenclature_id ?? r.fixed_asset_id ?? '', r);
    return m;
  }, [previewAccountsQ.data]);
  // 2026-07-27: попап «створити з рахунку» (підстава: рахунок/замовлення).
  const [basisPickerOpen, setBasisPickerOpen] = useState(false);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  // Активные заказы покупателя (для селекта «На основании заказа»)
  const customerOrdersQ = useQuery({
    queryKey: ['customer-orders-active', form.organization_id, form.contractor_id],
    queryFn: () => customerOrders.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      posted: true,
      limit: 100,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  // 0372 (Порція 2 моделі договорів): дефолт-ставка ПДВ З ДОГОВОРУ (вкладка
  // «Податковий облік» картки) має пріоритет над типовою 20%.
  const contractVatRateId = useMemo(() => {
    const c = (contractsQ.data ?? []).find((x: any) => x.id === form.contract_id);
    return (c as any)?.vat_rate_id ?? '';
  }, [contractsQ.data, form.contract_id]);
  const defaultVatId = useMemo(() => {
    if (contractVatRateId) return contractVatRateId;
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data, contractVatRateId]);
  // 2026-06-02: default TAXABLE для tax_purpose у новий рядок.
  const defaultTaxPurposeId = useMemo(
    () => taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null,
    [taxPurposesList.data],
  );
  // 2026-06-02: ховаємо колонки ПДВ якщо org НЕ платник.
  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  // ч.1374: колонки ПДВ — платник І «враховувати ПДВ» у документі.
  // ч.1383: платник ПДВ — на дату документа; збережений документ з ПДВ лишається з ПДВ,
  // навіть якщо організація потім перестала бути платником.
  const docVatPayer = vatPayerOn(currentOrg, form.document_date)
    || (!isNew && form.vat_flags.vat_considered);
  const showVatColumns = docVatPayer && form.vat_flags.vat_considered;
  const rateOf = (vatRateId: string | null | undefined) =>
    vatList.data?.find((v) => v.id === vatRateId)?.rate_percent ?? 0;
  const vatFlagsTouched = useRef(false);
  useEffect(() => {
    if (!isNew || copy.source || vatFlagsTouched.current || !currentOrg) return;
    const next = orgVatDefaults(currentOrg, form.document_date);
    setForm((f) => (f.vat_flags.vat_considered === next.vat_considered
      && f.vat_flags.prices_include_vat === next.prices_include_vat && f.vat_flags.vat_auto === next.vat_auto)
      ? f : { ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, copy.source, currentOrg, vatList.data, form.document_date]);
  function applyVatFlags(next: DocVatFlags) {
    vatFlagsTouched.current = true;
    setForm((f) => ({ ...f, vat_flags: next, items: recalcRows(f.items, next, rateOf) }));
  }
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveDefaultVatId = showVatColumns ? defaultVatId : exemptVatId;

  // Карта nomenclature_id -> use_series. Для рядків, де use_series=true,
  // показуємо селект серії.
  const useSeriesMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_series));
    return m;
  }, [nomList.data]);
  // Етап 2.1: карта nomenclature_id -> vat_rate_id (ставка ПДВ з картки).
  // При виборі номенклатури підставляємо ставку за замовчуванням у рядок.
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.vat_rate_id) m.set(n.id, n.vat_rate_id);
    }
    return m;
  }, [nomList.data]);
  // ч.1423: податкове призначення з картки; немає на картці — типове «оподатковувані».
  const taxPurposeByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.tax_purpose_id) m.set(n.id, n.tax_purpose_id);
    }
    return m;
  }, [nomList.data]);
  // 2026-06-02: soft-warning — балансі на дату документа для перевірки
  // що quantity не перевищує доступну на складі.
  const balanceForWarningQ = useQuery({
    queryKey: ['goods-balance-warning', form.organization_id, form.document_date, form.warehouse_id],
    queryFn: () => import('@/api/documents').then((m) =>
      m.reports.goodsBalance({
        organization_id: form.organization_id,
        on_date: form.document_date,
        warehouse_id: form.warehouse_id || undefined,
      })
    ),
    enabled: !!form.organization_id && !!form.document_date && !!form.warehouse_id,
  });
  const availableByNomId = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of balanceForWarningQ.data?.rows ?? []) {
      // Якщо одна номенклатура є у кількох складах і warehouse не обрано —
      // підсумовуємо. У більшості випадків warehouse_id обраний.
      const prev = m.get(r.nomenclature_id) ?? 0;
      m.set(r.nomenclature_id, prev + Number(r.quantity));
    }
    return m;
  }, [balanceForWarningQ.data]);

  // Запити серій по тих номенклатурах, які зустрічаються в рядках
  // і мають use_series=true.
  const seriesNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useSeriesMap.get(it.nomenclature_id)) {
        s.add(it.nomenclature_id);
      }
    }
    return Array.from(s);
  }, [form.items, useSeriesMap]);

  const seriesQueries = useQueries({
    queries: seriesNomIds.map((nid) => ({
      queryKey: ['nomenclature-series', nid],
      queryFn: () => refs.nomenclatureSeries(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const seriesByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    seriesNomIds.forEach((nid, idx) => {
      const q = seriesQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [seriesNomIds, seriesQueries]);

  useEffect(() => {
    if (isNew && !copy.source) {
      // 2026-07-27: при створенні «на підставі» (?basis=) дефолт-орг НЕ
      // підставляємо — організація прийде З ДОКУМЕНТА-ПІДСТАВИ (гонка двох
      // ефектів давала дефолт замість орг рахунку).
      const firstOrg = basisId ? '' : (availableOrgs[0]?.id ?? '');
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        // 2026-07-27 (клас «порожній рядок у payload → 422», як у замовленні):
        // порожній рядок НЕ додаємо — рядки додаються кнопками «Додати».
        items: f.items,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id ?? '',
        customer_order_id: d.customer_order_id ?? '',
        allow_overshipment: d.allow_overshipment,
        vat_flags: {
          vat_considered: d.vat_considered ?? true,
          prices_include_vat: d.prices_include_vat,
          vat_auto: d.vat_auto ?? true,
        },
        advance_offset_mode: d.advance_offset_mode ?? 'AUTO',
        delivery_address: d.delivery_address ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          fixed_asset_id: it.fixed_asset_id ?? null,
          fa_primary_cost_snapshot: it.fa_primary_cost_snapshot ?? null,
          fa_accumulated_depreciation_snapshot: it.fa_accumulated_depreciation_snapshot ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          cost_amount: it.cost_amount ? String(it.cost_amount) : undefined,
          service_description: it.service_description ?? '',
          account_code: it.account_code ?? null,
          revenue_account_code: it.revenue_account_code ?? null,
          cost_account_code: it.cost_account_code ?? null,
          // TD-B-P5.5.3 (2026-06-01).
          tax_purpose_id: it.tax_purpose_id ?? null,
          // 2026-06-02: auto-fill uom_id з base_uom_id.
          uom_id: it.uom_id ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка до замовлень і підстав
        setForm((f) => ({
          ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), customer_order_id: EMPTY_FORM.customer_order_id,
          // собівартість і знімки вартості активу — результат проведення зразка, у копії їх рахує нове проведення
          items: f.items.map((it) => ({ ...it, cost_amount: undefined, fa_primary_cost_snapshot: null, fa_accumulated_depreciation_snapshot: null })),
        }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs, defaultVatId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // ч.948 (рішення власника): «створити ↗» біля контрагента і договору
  // відкриває ПОПАП просто на сторінці документа (як у ПКО/РКО).
  const [quickContractor, setQuickContractor] = useState(false);
  const [quickContract, setQuickContract] = useState(false);
  const contractorNameForQuick = (buyerList.data?.items ?? []).find((c: { id: string; name: string }) => c.id === form.contractor_id)?.name ?? '';

  // ПН, вже створена з цієї реалізації (base_document_id) — без дублів.
  const [pnBusy, setPnBusy] = useState(false);
  const pnListQ = useQuery({
    queryKey: ['pn-for-sale', id],
    queryFn: () => taxInvoicesOutgoing.list({ limit: 500 }),
    // ч.1352: ПН буває раніше за проведення (створена з неї реалізація, ч.1347 — ПН зі
    // збереженої) — шукаємо наявну для будь-якої збереженої реалізації, інакше «створити ПН»
    // зробив би другу.
    enabled: !isNew,
  });
  const existingPn = useMemo(() => {
    return (pnListQ.data?.items ?? [])
      .find((p) => p.base_document_id === id && !p.deletion_mark) ?? null;
  }, [pnListQ.data, id]);
  async function createPnFromSale() {
    if (!id) return;
    setPnBusy(true);
    try {
      const pn = await taxInvoicesOutgoing.createFromSale({
        organization_id: form.organization_id,
        base_document_type: 'doc.goods_sales',
        base_document_id: id,
      });
      navigate(`/tax-invoices-outgoing/${pn.id}`);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setPnBusy(false);
    }
  }

  // 2026-07-27 (UX як в УТП): якщо в організації РІВНО ОДИН склад —
  // підставляємо його автоматично (склад обов'язковий для товарних рядків).
  useEffect(() => {
    if (isPosted || form.warehouse_id) return;
    if (warehousesQ.data?.length === 1) {
      setForm((f) => f.warehouse_id ? f : { ...f, warehouse_id: warehousesQ.data![0]!.id });
    }
  }, [warehousesQ.data, isPosted, form.warehouse_id]);


  // П1 (2026-07-23): /goods-sales/new?basis=<order_id> — кнопка «створити
  // реалізацію» із замовлення/рахунку: шапка з підстави + рядки-залишки.
  const basisApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !basisId || basisApplied.current) return;
    // Довідники потрібні для перерахунку рядків (ставки/ОВ/призначення ПДВ).
    if (!vatList.data || !nomList.data || !taxPurposesList.data) return;
    basisApplied.current = true;
    void (async () => {
      try {
        const order = await customerOrders.get(basisId);
        setForm((f) => ({
          ...f,
          organization_id: order.organization_id,
          contractor_id: order.contractor_id,
          contract_id: order.contract_id ?? '',
          warehouse_id: order.warehouse_id ?? f.warehouse_id,
          customer_order_id: order.id,
          // ч.1374: ціни з ПДВ / без — як у рахунку-підставі.
          vat_flags: {
            vat_considered: order.vat_considered ?? true,
            prices_include_vat: order.prices_include_vat ?? false,
            vat_auto: order.vat_auto ?? true,
          },
          items: orderToRows(order),
        }));
        vatFlagsTouched.current = true;
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, basisId, vatList.data, nomList.data, taxPurposesList.data]);

  // ч.1352: рядки реалізації — з рядків ПН (товари/послуги; рядки ОЗ/НМА накладної
  // переносяться як звичайні, бухгалтер уточнить вкладку).
  const pnApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !pnBasisId || pnApplied.current) return;
    if (!vatList.data || !taxPurposesList.data) return;
    pnApplied.current = true;
    void (async () => {
      try {
        const pn = await taxInvoicesOutgoing.get(pnBasisId);
        const rows: GoodsSaleItem[] = pn.items
          .filter((it) => it.nomenclature_id && Number(it.quantity) > 0)
          .map((it, idx) => {
            const base = Number(it.base_amount);
            const vatAmt = Number(it.vat_amount);
            return {
              ...newRow(idx + 1, it.vat_rate_id, defaultTaxPurposeId),
              nomenclature_id: it.nomenclature_id,
              quantity: String(Number(it.quantity)),
              price: String(Number(it.price_without_vat)),
              amount_without_vat: base.toFixed(2),
              vat_rate_id: it.vat_rate_id,
              vat_amount: vatAmt.toFixed(2),
              total_amount: (base + vatAmt).toFixed(2),
            };
          });
        setForm((f) => ({
          ...f,
          organization_id: pn.organization_id,
          contractor_id: pn.contractor_id ?? f.contractor_id,
          contract_id: pn.contract_id ?? f.contract_id,
          document_date: pn.invoice_date ? `${pn.invoice_date}T12:00` : f.document_date,
          items: rows.length ? rows : f.items,
        }));
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, pnBasisId, vatList.data, taxPurposesList.data]);
  const bankApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !bankReceiptId || bankApplied.current) return;
    bankApplied.current = true;
    void (async () => {
      try {
        const p = await bankPrefill('receipt', bankReceiptId);
        setForm((f) => ({
          ...f,
          organization_id: p.organization_id || f.organization_id,
          contractor_id: p.contractor_id || f.contractor_id,
          contract_id: p.contract_id || f.contract_id,
          document_date: p.document_date || f.document_date,
          comment: f.comment || paymentNote('receipt', p),
        }));
      } catch (e) {
        setError(extractError(e));
      }
    })();
  }, [isNew, bankReceiptId]);

  // 0445: «продаж» з картки ОЗ — рядок-ОЗ + вкладка «ОЗ».
  const assetApplied = useRef(false);
  useEffect(() => {
    if (!isNew || !assetIdParam || assetApplied.current || !vatList.data) return;
    assetApplied.current = true;
    setForm((f) => ({
      ...f,
      organization_id: orgIdParam || f.organization_id,
      items: [...f.items, {
        ...newRow(f.items.length + 1, effectiveDefaultVatId, defaultTaxPurposeId),
        fixed_asset_id: assetIdParam, quantity: '1',
        revenue_account_code: '712', cost_account_code: '943',
      }],
    }));
    setLinesTab('assets');
  }, [isNew, assetIdParam, orgIdParam, vatList.data]);

  // П1: УТП-дефолти договору — автовибір основного (is_main) + ціна за
  // типом цін договору при виборі номенклатури.
  useMainContractAutoSelect({
    // 2026-07-25: і при редагуванні DRAFT — зміна контрагента скидає договір,
    // основний нового контрагента підставляється (posted-док не зачіпається:
    // у нього contract_id завжди заповнений — хук мовчить).
    enabled: true,
    contractorId: form.contractor_id,
    contractId: form.contract_id,
    contracts: contractsQ.data,
    onSelect: (cid) => setForm((f) => (f.contract_id ? f : { ...f, contract_id: cid })),
  });
  const { priceFor } = useContractPriceLookup(contractsQ.data, form.contract_id,
    form.organization_id, form.document_date);
  // П2: дефолтний % знижки за договором (0372/0375) — підставляється у нові рядки.
  const contractDiscount = useMemo(() => {
    const c = (contractsQ.data ?? []).find((x) => x.id === form.contract_id);
    const d = Number(c?.discount_percent ?? 0);
    return d > 0 ? String(d) : '';
  }, [contractsQ.data, form.contract_id]);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;
  const totalCost = form.items.reduce((s, e) => s + Number(e.cost_amount || 0), 0);

  function docPayload() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id || null,
      customer_order_id: form.customer_order_id || null,
      allow_overshipment: form.allow_overshipment,
      ...form.vat_flags,
      advance_offset_mode: form.advance_offset_mode,
      delivery_address: form.delivery_address || null,
      comment: form.comment || null,
      project_id: form.project_id,
      items: buildPayloadItems() ?? [],
    };
  }

  const create = useMutation({
    mutationFn: async () => {
      const doc = await goodsSales.create(docPayload());
      await linkPn(doc.id);
      await linkBank(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
      navigate(`/goods-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updatePayload = () => ({
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    contractor_id: form.contractor_id,
    contract_id: form.contract_id || null,
    warehouse_id: form.warehouse_id || null,
    customer_order_id: form.customer_order_id || null,
    allow_overshipment: form.allow_overshipment,
    ...form.vat_flags,
    advance_offset_mode: form.advance_offset_mode,
    delivery_address: form.delivery_address || null,
    comment: form.comment || null,
    project_id: form.project_id,
    items: buildPayloadItems() ?? [],
  });
  const update = useMutation({
    mutationFn: () => goodsSales.update(id!, updatePayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-sale', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    // ч.1267: «провести» наявного документа спершу зберігає правки форми — інакше
    // проводився старий збережений вміст, а зміни рядків і цін губились мовчки.
    mutationFn: async () => {
      if (!isPosted) await goodsSales.update(id!, updatePayload());
      return goodsSales.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sale', id] });
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  // 2026-07-27 (баг «doc_id must be valid UUID» на НОВОМУ документі):
  // «провести» при isNew мусить СПОЧАТКУ створити документ, потім провести
  // (post.mutate() бив /goods-sales/new/post). Дзеркало інших редакторів.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsSales.create(docPayload());
      await linkPn(doc.id);
      await linkBank(doc.id);
      try {
        await goodsSales.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-sales/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
      navigate(`/goods-sales/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsSales.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sale', id] });
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsSales.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-sales'] });
      navigate('/goods-sales');
    },
  });

  // Block 7.6: pre-POST commissioner detection. Якщо є активний RECEIVED
  // commission_contract з contractor → показуємо modal; інакше POST одразу.
  async function onPostClick() {
    if (!form.organization_id || !form.contractor_id) {
      (isNew ? createAndPost : post).mutate();
      return;
    }
    try {
      const check = await commissionerWarning.check(
        form.organization_id, form.contractor_id,
      );
      if (check.is_commissioner_relationship) {
        setCommWarning(check);
        return; // wait for user choice via modal
      }
    } catch {
      // detection-fail — не блокуємо POST, fallback на стандартний flow.
    }
    (isNew ? createAndPost : post).mutate();
  }

  function onCommissionerChoice(choice: CommissionerWarningChoice) {
    setCommWarning(null);
    if (choice === 'cancel') return;
    if (choice === 'create_commission') {
      navigate('/documents/commission-sales/new', {
        state: {
          counterparty_id: form.contractor_id,
          organization_id: form.organization_id,
          items: form.items,
        },
      });
      return;
    }
    // 'continue' → proceed with goods_sale POST (audit note додається у backend)
    (isNew ? createAndPost : post).mutate();
  }

  function setItem(i: number, patch: Partial<GoodsSaleItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch
            || 'discount_percent' in patch || 'vat_amount' in patch) {
          // П2: знижка рядка — сума без ПДВ вже ПІСЛЯ знижки (як в УТП).
          // ч.1374: ціни з ПДВ / без, ПДВ вручну — за ознаками документа.
          const a = lineAmounts(Number(next.quantity), Number(next.price), rateOf(next.vat_rate_id), f.vat_flags, {
            discountPercent: Number(next.discount_percent) || 0,
            manualVat: 'vat_amount' in patch ? Number(next.vat_amount) : null,
          });
          next.discount_amount = a.discount_amount;
          next.amount_without_vat = a.amount_without_vat;
          next.vat_amount = a.vat_amount;
          next.total_amount = a.total_amount;
        }
        return next;
      }),
    }));
  }
  const isServiceByNomId = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const n of nomList.data?.items ?? []) m.set(n.id, !!n.is_service);
    return m;
  }, [nomList.data]);
  const rowIsService = (it: GoodsSaleItem) =>
    it.nomenclature_id ? (isServiceByNomId.get(it.nomenclature_id) ?? false) : false;
  // 2026-07-31 (рішення власника, як в 1С): рядок-обладнання = рахунок 15х
  // (капітальні інвестиції) — продаж обладнання, не введеного в
  // експлуатацію; дохід 712 / собівартість 943 (авто-перебивки).
  const rowIsEquip = (it: GoodsSaleItem) =>
    !it.fixed_asset_id && ((it.account_code ?? '') as string).startsWith('15');
  // 0445: рядок-ОЗ (продаж основного засобу) — окрема вкладка «ОЗ».
  const rowIsFa = (it: GoodsSaleItem) => !!it.fixed_asset_id;
  const goodsCount = form.items.filter((it) => it.nomenclature_id && !rowIsService(it) && !rowIsEquip(it)).length;
  const servicesCount = form.items.filter((it) => rowIsService(it)).length;
  const equipCount = form.items.filter((it) => it.nomenclature_id && rowIsEquip(it) && !rowIsService(it)).length;
  const faCount = form.items.filter(rowIsFa).length;
  // Порожні рядки (без номенклатури) видно на вкладках Товари/Послуги.
  const visibleRows = form.items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => {
      if (linesTab === 'assets') return rowIsFa(it);
      if (rowIsFa(it)) return false;
      if (linesTab === 'equipment') return !!it.nomenclature_id && rowIsEquip(it) && !rowIsService(it);
      if (!it.nomenclature_id) return true;
      if (rowIsEquip(it)) return false;
      return linesTab === 'services' ? rowIsService(it) : !rowIsService(it);
    });

  // ч.750 (рішення власника): «Разом» — по ПОТОЧНІЙ вкладці, «Усього» — по документу.
  const tabWithoutVat = visibleRows.reduce((s, { it }) => s + Number(it.amount_without_vat || 0), 0);
  const tabVat = visibleRows.reduce((s, { it }) => s + Number(it.vat_amount || 0), 0);
  const tabCost = visibleRows.reduce((s, { it }) => s + Number(it.cost_amount || 0), 0);

  // Порожні рядки (без номенклатури/ОЗ) відкидаємо; заповнені без кількості —
  // явна помилка (НЕ мовчазний víдкид). Перенумерація line_number.
  function buildPayloadItems(): GoodsSaleItem[] | null {
    const filled = form.items.filter((it) => it.nomenclature_id || it.fixed_asset_id);
    if (filled.length === 0) {
      setError('Додайте хоча б один товар, послугу або ОЗ.');
      return null;
    }
    const noQty = filled.find((it) => !(Number(it.quantity) > 0));
    if (noQty) {
      const nom = nomList.data?.items.find((n) => n.id === noQty.nomenclature_id);
      setError(`Вкажіть кількість: «${nom?.name ?? (noQty.fixed_asset_id ? faLabel(noQty.fixed_asset_id) : 'рядок ' + noQty.line_number)}».`);
      return null;
    }
    return filled.map((it, idx) => ({
      ...it, line_number: idx + 1,
      nomenclature_id: it.nomenclature_id || null,
      fixed_asset_id: it.fixed_asset_id || null,
    }));
  }

  function addFaFromPicker(asset: { id: string; residual_value: string }) {
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        ...newRow(f.items.length + 1, effectiveDefaultVatId, defaultTaxPurposeId),
        fixed_asset_id: asset.id, quantity: '1',
        vat_rate_id: showVatColumns ? defaultVatId : exemptVatId,
        revenue_account_code: '712', cost_account_code: '943',
      }],
    }));
    setFaPickerOpen(false);
  }

  // ч.675: підбір мультивибором — усі позначені рядки додаються разом
  // (попап закривається сам по «додати обрані»).
  // opts (ч.927) передає лише новий попап підбору товарів: price — ціна, по якій
  // клікнули (null — без ціни); без opts (послуги) — як раніше, ціна за договором.
  function addRowsFromPicker(pickedRows: GoodsBalanceRow[], opts?: { price: string | null }) {
    setForm((f) => ({
      ...f,
      items: [...f.items, ...pickedRows.map((picked, k): GoodsSaleItem => ({
        ...newRow(f.items.length + 1 + k, effectiveDefaultVatId, defaultTaxPurposeId),
        nomenclature_id: picked.nomenclature_id,
        // П2: знижка з договору.
        discount_percent: contractDiscount || '0',
        ...(opts?.price ? { price: opts.price } : {}),
        // Auto-fill uom_id (2026-06-02).
        // uom_id: NULL = базова одиниця (FK на nomenclature_uoms, НЕ класифікатор!)
        uom_id: null,
        // ч.661 (рішення власника): джерело істини — СКЛАД РЯДКА. Підбір
        // завжди пише явний склад у рядок; шапка — лише дефолт, її не чіпаємо.
        warehouse_id: picked.warehouse_id || f.warehouse_id || null,
        // Етап 2.1: ставка ПДВ з картки (fallback — дефолт документа).
        // 2026-07-27: режим орг ПЕРВИННИЙ — неплатник завжди «Без ПДВ»
        // (ставка картки — юридична властивість, діє лише у платника).
        vat_rate_id: showVatColumns
          ? (vatRateByNomId.get(picked.nomenclature_id) ?? defaultVatId)
          : exemptVatId,
        tax_purpose_id: taxPurposeByNomId.get(picked.nomenclature_id) ?? defaultTaxPurposeId,
        // Вкладка «Обладнання»: 152 (запаси) / 712 (дохід) / 943 (собівартість).
        ...(linesTab === 'equipment'
          ? { account_code: '152', revenue_account_code: '712', cost_account_code: '943' }
          : {}),
      }))],
    }));
    if (opts) return;  // ч.927: попап товарів — ціна лише кліком по ціні
    // П1: автопідстановка ціни за типом цін договору (як в УТП). Кількість
    // у новому рядку 0 → суми перерахуються у setItem при вводі кількості.
    for (const nid of new Set(pickedRows.map((p) => p.nomenclature_id))) {
      void priceFor(nid).then((p) => {
        if (p == null) return;
        setForm((f) => ({
          ...f,
          items: f.items.map((row) =>
            row.nomenclature_id === nid && Number(row.price) === 0
              ? { ...row, price: p }
              : row),
        }));
      });
    }
  }
  function addRowFromPicker(picked: GoodsBalanceRow) {
    addRowsFromPicker([picked]);
    setPickerOpen(false);
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Строки з заказа покупця: остаток (quantity - quantity_shipped) на рядок,
  // повністю відвантажені пропускаються. Спільне ядро для «Заповнити з
  // підстави» і відкриття /goods-sales/new?basis=<order_id> (П1).
  function orderToRows(order: Awaited<ReturnType<typeof customerOrders.get>>): GoodsSaleItem[] {
    return order.items
      .map((it, idx) => {
        const remaining = Number(it.quantity) - Number(it.quantity_shipped ?? 0);
        if (remaining <= 0) return null;
        const price = Number(it.price);
        // П2: знижка рядка замовлення переноситься у реалізацію.
        const disc = Number(it.discount_percent ?? 0);
        const gross = remaining * price;
        const discAmt = (gross * disc) / 100;
        const awv = gross - discAmt;
        const vat = vatList.data?.find((v) => v.id === it.vat_rate_id);
        const rate = vat?.rate_percent ?? 0;
        const vatAmt = (awv * rate) / 100;
        const row: GoodsSaleItem = {
          line_number: idx + 1,
          nomenclature_id: it.nomenclature_id,
          // 0445: рядок-ОЗ рахунку → рядок-ОЗ реалізації.
          fixed_asset_id: it.fixed_asset_id ?? null,
          quantity: String(remaining),
          price: String(price),
          discount_percent: disc > 0 ? String(disc) : '0',
          discount_amount: discAmt.toFixed(2),
          amount_without_vat: awv.toFixed(2),
          // Ставка з замовлення; якщо порожня — з картки номенклатури (Етап 2.1).
          vat_rate_id: showVatColumns
            ? (it.vat_rate_id ?? vatRateByNomId.get(it.nomenclature_id ?? '') ?? defaultVatId)
            : exemptVatId,
          vat_amount: vatAmt.toFixed(2),
          total_amount: (awv + vatAmt).toFixed(2),
          uom_id: null,
          // 2026-07-31 (власник: «в рахунку склад у рядку, а в реалізації
          // "з шапки"»): склад РЯДКА рахунку/замовлення переноситься у
          // рядок реалізації (постинг і так пріоритезує рядок над шапкою).
          warehouse_id: (it as { warehouse_id?: string | null }).warehouse_id ?? null,
          // Рядок-обладнання рахунку (15х) → авто 152/712/943.
          ...((((it as { account_code?: string | null }).account_code ?? '').startsWith('15') || it.fixed_asset_id)
            ? { account_code: it.fixed_asset_id ? null : (it as { account_code?: string | null }).account_code,
                revenue_account_code: '712', cost_account_code: '943' }
            : {}),
          tax_purpose_id: taxPurposeByNomId.get(it.nomenclature_id ?? '') ?? defaultTaxPurposeId,
        };
        return row;
      })
      .filter((x): x is GoodsSaleItem => x !== null);
  }

  async function fillFromOrder(orderId?: string) {
    const oid = orderId ?? form.customer_order_id;
    if (!oid) return;
    try {
      const order = await customerOrders.get(oid);
      const newItems = orderToRows(order);
      if (newItems.length === 0) {
        alert('Замовлення повністю відвантажено — немає що додавати.');
        return;
      }
      setForm((f) => ({ ...f, items: newItems }));
    } catch (e) {
      setError(extractError(e));
    }
  }

  // UX α: warning якщо новий документ а user не має sales.realization.create
  // у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('gs.new')}</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати реалізацію у жодній з ваших організацій
            ({totalAccessibleOrgs}). Зверніться до адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-sales')}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  // ч.853: «ще дії» → права колонка «Повʼязані дії».
  const railActions = [
              {
                // ч.1352 (обернений ланцюг): рахунок покупцю З реалізації (пост-фактум).
                label: 'створити рахунок',
                title: isNew ? 'Спершу збережіть реалізацію'
                  : 'Рахунок на оплату з рядків цієї реалізації; документи звʼязуються',
                disabled: isNew,
                onClick: () => navigate(`/customer-invoices/new?from_sale=${id}`),
              },
              {
                // ч.1347: «на підставі» — зі збереженої реалізації, проведення не чекаємо.
                label: 'створити ттн',
                title: isNew ? 'Спершу збережіть реалізацію'
                  : 'Створити ТТН на основі цієї Реалізації',
                disabled: isNew,
                onClick: () => navigate(`/tnn/new?sale_id=${id}`),
              },
              existingPn ? {
                label: 'відкрити ПН',
                title: `ПН №${existingPn.invoice_number} вже створена з цієї реалізації`,
                onClick: () => navigate(`/tax-invoices-outgoing/${existingPn.id}`),
              } : {
                label: pnBusy ? 'ПН створюється…' : 'створити ПН',
                title: isNew ? 'Спершу збережіть реалізацію'
                  : 'Створити податкову накладну на основі цієї реалізації',
                disabled: pnBusy || isNew,
                onClick: () => void createPnFromSale(),
              },
  ];

  // ч.853: прогрес — ЛИШЕ обовʼязкові поля.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'party', title: 'Покупець і договір', required: 1,
      filled: form.contractor_id ? 1 : 0 },
    { id: 'wh', title: 'Склад і рахунки', required: 1,
      filled: form.warehouse_id ? 1 : 0 },
    { id: 'items', title: 'Товари / Послуги', required: 1,
      filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.700 (рішення власника): інфо-рядок (дт-кт · історія · звʼязані ·
          стан) — В ОДНІЙ СТРОЦІ з назвою сторінки. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('gs.new') : `${t('gs.doc')}: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/goods-sales')}
            onSave={() => { if (!buildPayloadItems()) return; (isNew ? create.mutate() : update.mutate()); }}
            saveDisabled={create.isPending || update.isPending || !has('sales.realization.create')}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            onPost={() => { if (!buildPayloadItems()) return; onPostClick(); }}
            postPending={post.isPending || createAndPost.isPending}
            postDisabled={form.items.length === 0 || !has('sales.realization.post')}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            deleteDisabled={!has('sales.realization.delete')}
            /* ч.857 (уточнення власника): форма залежить від складу ТЧ —
               САМІ послуги → друк одразу АКТ; немає послуг → видаткова;
               ЗМІШАНИЙ документ → вибір із двох форм (як і було на
               друкованій сторінці: ?form=act фільтрує рядки-послуги). */
            {...(servicesCount > 0 && (goodsCount + equipCount + faCount) > 0
              ? {
                  printOptions: [
                    { label: 'видаткова накладна (товари)',
                      onClick: () => navigate(`/goods-sales/${id}/print`) },
                    { label: 'акт наданих послуг',
                      onClick: () => navigate(`/goods-sales/${id}/print?form=act`) },
                  ],
                }
              : { onPrint: () => navigate(`/goods-sales/${id}/print`) })}
            entriesDoc={{ type: 'doc.goods_sales', id: isNew ? '' : id! }}
            // ч.853: доп-дії — у правій колонці («Повʼязані дії»).
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      {/* ч.853 (новий дизайн, Закон #10): блоки + права рейка. */}
      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-sales/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_sales', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>{t('gs.organization')} *</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>{t('gs.number')}</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('gs.date')} *</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        {/* 2026-08-07 (рішення власника): «Напрям» — у перший рядок. */}
        <ProjectField span={3}
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>

      </DocBlock>

      <DocBlock index={2} title="Покупець і договір">
      {/* 2026-07-27 (рішення власника): рядок 2 як у рахунку —
          Покупець | Договір | «створити з рахунку» (попап підстав). */}
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>{t('gs.contractor')} *
            <QuickCreateLink title="Створити нового контрагента"
              disabled={isPosted}
              onClick={() => setQuickContractor(true)} />
          </label>
          <SearchSelect
            items={((buyerList.data?.items ?? [])).map((c) => ({
              id: c.id, label: c.name, hint: c.edrpou ?? null,
            }))}
            value={form.contractor_id}
            onChange={(id) => setForm({ ...form, contractor_id: id, contract_id: '',
              customer_order_id: '' })}
            disabled={isPosted || !!form.customer_order_id}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>
            {t('gs.contract')}{' '}
            <small style={{ color: 'var(--muted)' }}>(авто вкл.)</small>
            <QuickCreateLink
              disabled={isPosted || !form.contractor_id}
              title={form.contractor_id ? 'Створити договір цього контрагента' : 'Спершу оберіть контрагента'}
              onClick={() => setQuickContract(true)} />
          </label>
          <select value={form.contract_id}
                  onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
                  disabled={isPosted || !form.contractor_id || !!form.customer_order_id}>
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{contractLabel(c)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          {/* ч.855 (рішення власника): «Підстава» — НАЗВА поля (зверху),
              сам документ — під нею. */}
          <label>{form.customer_order_id ? 'Підстава' : '\u00A0'}</label>
          {/* 2026-07-31 (рішення власника): звʼязок-підстава блокує
              Покупець/Договір/кнопку; ✕ знімає звʼязок і очищає рядки. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {/* ч.854: кнопка — лише поки немає звʼязку; зі звʼязком показуємо
                саму підставу з «×» (рішення власника). */}
            {!form.customer_order_id && (
              <button type="button" className="btn-doc"
                onClick={() => setBasisPickerOpen(true)}
                disabled={isPosted || !form.contractor_id || !form.contract_id}
                title={!form.contractor_id || !form.contract_id
                  ? 'Спершу оберіть покупця і договір'
                  : 'Заповнити реалізацію з активного рахунку/замовлення покупця'}>
                <DocIcon />створити з рахунку
              </button>
            )}
            {form.customer_order_id && (() => {
              const basisDoc = (customerOrdersQ.data?.items ?? [])
                .find((o) => o.id === form.customer_order_id);
              const basisRoute = basisDoc?.order_kind === 'ORDER'
                ? `/customer-orders/${form.customer_order_id}`
                : `/customer-invoices/${form.customer_order_id}`;
              return (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <a style={{ cursor: 'pointer', color: 'var(--primary)' }}
                    onClick={() => navigate(basisRoute)}>
                    {basisDoc?.order_kind === 'ORDER' ? 'замовлення' : 'рахунок'}
                    {basisDoc
                      ? ` №${basisDoc.document_number} від ${new Date(basisDoc.document_date).toLocaleDateString('uk-UA')}`
                      : ''}
                  </a>
                  {!isPosted && (
                    <button type="button" className="btn-x"
                      onClick={() => setForm({ ...form, customer_order_id: '', items: [] })}
                      title="Зняти звʼязок з рахунком і очистити рядки">×</button>
                  )}
                </span>
              );
            })()}
          </div>
        </div>
      </div>


      </DocBlock>

      <DocBlock index={3} title="Склад і рахунки">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Склад за замовчуванням *</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
            ))}
          </select>
        </div>
      </div>


      </DocBlock>

      <DocBlock index={4} title="Товари / Послуги"
        actions={!isPosted ? (linesTab === 'assets' ? (
          <button className="primary" onClick={() => setFaPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id ? 'Спершу оберіть юрособу' : 'Підбір ОЗ в експлуатації'}>
            + вибрати ОЗ
          </button>
        ) : (<>
          <VatFlagsButton value={form.vat_flags} onChange={applyVatFlags}
            isVatPayer={docVatPayer} />
          <button onClick={() => setQuickNomOpen(true)} className="btn-doc"
            title="Створити нову номенклатуру (мінімум полів) і одразу підставити рядком">
            <DocIcon />створити номенклатуру
          </button>
          <button className="primary" onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id
              ? 'Спершу оберіть юрособу'
              : linesTab === 'services'
                ? 'Підбір послуги з довідника'
                : 'Підбір товару з залишків з врахуванням складу'}>
            {linesTab === 'services' ? '+ додати послугу' : '+ додати товар'}
          </button>
        </>)) : undefined}>
      {/* 2026-07-26 (рішення власника): «Реалізація товарів і послуг» — як в
          УТП, ОДИН документ з вкладками Товари/Послуги (дзеркало рахунку).
          Рядки-послуги проводяться на 703 без складу (backend уже вмів). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div className="seg-tabs">
          <button type="button" className={viewTab === 'lines' && linesTab === 'goods' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('goods'); }}>Товари ({goodsCount})</button>
          <button type="button" className={viewTab === 'lines' && linesTab === 'services' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('services'); }}>Послуги ({servicesCount})</button>
          <button type="button" className={viewTab === 'lines' && linesTab === 'equipment' ? 'active' : ''}
            onClick={() => { setViewTab('lines'); setLinesTab('equipment'); }}>Обладнання ({equipCount})</button>
          <button type="button" className={viewTab === 'lines' && linesTab === 'assets' ? 'active' : ''}
            title="Продаж основних засобів, що в експлуатації (131/286/943 + дохід 712)"
            onClick={() => { setViewTab('lines'); setLinesTab('assets'); }}>ОЗ ({faCount})</button>
        </div>
        {/* 2026-07-27 (рішення власника): бухгалтер може перебити рахунки
            бух/податкового обліку по рядках (як у договорах). */}
        <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
          <button type="button" className={viewTab === 'accounts' ? 'active' : ''}
            onClick={() => setViewTab('accounts')}>Рахунки обліку</button>
          <button type="button" className={viewTab === 'tax' ? 'active' : ''}
            onClick={() => setViewTab('tax')}>Податковий облік</button>
        </div>
      </div>
      {viewTab === 'accounts' && (
        <div style={{ marginTop: '0.5rem' }}>
          {/* Галка авансу переїхала сюди (рішення власника 2026-07-27). */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isPosted ? 'default' : 'pointer', margin: '0.5rem 0' }}>
            <input type="checkbox"
              checked={form.advance_offset_mode === 'NONE'}
              disabled={isPosted}
              onChange={(e) => setForm({ ...form, advance_offset_mode: e.target.checked ? 'NONE' : 'AUTO' })} />
            <span>не зараховувати аванс автоматично
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                (типово аванс за договором зараховується при проведенні)
              </span>
            </span>
          </label>
          <TchTable>
            <thead>
              <tr style={{ textTransform: 'none' }}>
                <th>Номенклатура</th>
                <th style={{ width: 140 }}
                    title="Рахунок запасів рядка (порожньо — авто: картка/правила)">Рах. обліку</th>
                <th style={{ width: 140 }}
                    title="Рахунок доходу (порожньо — авто: 702/703/701/712)">Рах. доходу</th>
                <th style={{ width: 140 }}
                    title="Рахунок собівартості (порожньо — авто: 902/901/943)">Рах. собівартості</th>
              </tr>
            </thead>
            <tbody>
              {form.items.filter((it) => it.nomenclature_id || it.fixed_asset_id).map((it) => {
                const i = form.items.indexOf(it);
                const nom = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
                const pkey = it.nomenclature_id || it.fixed_asset_id || '';
                return (
                  <tr key={i}>
                    <td>{nom?.name ?? (it.fixed_asset_id ? `ОЗ: ${faLabel(it.fixed_asset_id)}` : '—')}</td>
                    <td>
                      <input value={it.account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { account_code: e.target.value || null })}
                        disabled={isPosted || !!nom?.is_service}
                        placeholder={previewByNom.get(pkey)?.account_code ?? ''}
                        title={it.fixed_asset_id ? 'Рахунок активу 10x (з групи ОЗ; знос 131, залишкова через 286)' : undefined}
                        style={{ width: '100%' }} />
                    </td>
                    <td>
                      <input value={it.revenue_account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { revenue_account_code: e.target.value || null })}
                        disabled={isPosted}
                        placeholder={previewByNom.get(pkey)?.revenue_account_code ?? ''}
                        style={{ width: '100%' }} />
                    </td>
                    <td>
                      <input value={it.cost_account_code ?? ''} maxLength={10}
                        onChange={(e) => setItem(i, { cost_account_code: e.target.value || null })}
                        disabled={isPosted || !!nom?.is_service}
                        placeholder={previewByNom.get(pkey)?.cost_account_code ?? ''}
                        style={{ width: '100%' }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TchTable>
          <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.4rem' }}>
            Сірим показано рахунки автоматичного підбору (картка/правила/типові) — впишіть свій код, щоб перебити.
          </p>
        </div>
      )}
      {viewTab === 'tax' && (
        <TchTable style={{ marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ textTransform: 'none' }}>
              <th>Номенклатура</th>
              <th style={{ width: 160 }}>Ставка ПДВ</th>
              <th style={{ width: 220 }}
                  title="Призначення ПДВ для пропорційного розподілу (ПКУ ст.199)">Призначення ПДВ</th>
            </tr>
          </thead>
          <tbody>
            {form.items.filter((it) => it.nomenclature_id || it.fixed_asset_id).map((it) => {
              const i = form.items.indexOf(it);
              const nom = nomList.data?.items.find((n) => n.id === it.nomenclature_id);
              return (
                <tr key={i}>
                  <td>{nom?.name ?? (it.fixed_asset_id ? `ОЗ: ${faLabel(it.fixed_asset_id)}` : '—')}</td>
                  <td>
                    <select value={it.vat_rate_id}
                      onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                      disabled={isPosted} style={{ width: '100%' }}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={it.tax_purpose_id ?? ''}
                      onChange={(e) => setItem(i, { tax_purpose_id: e.target.value || null })}
                      disabled={isPosted} style={{ width: '100%' }}>
                      <option value="">—</option>
                      {taxPurposesList.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {viewTab === 'lines' && (<>
      {linesTab === 'equipment' && (
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.5rem 0 0.25rem' }}>
          Продаж обладнання зі <b>152</b> (придбаного, але НЕ введеного в
          експлуатацію): дохід 712, собівартість 943 — авто. ОЗ, що ВЖЕ в
          експлуатації, продається на вкладці «ОЗ».
        </p>
      )}
      {linesTab === 'assets' && (
        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', margin: '0.5rem 0 0.25rem' }}>
          Продаж основного засобу з експлуатації (як «Передача ОЗ» в 1С): при
          проведенні знос Дт 131 — Кт 10x, залишкова Дт 286 — Кт 10x і
          Дт 943 — Кт 286, дохід Дт 361 — Кт 712, ПДВ Дт 712 — Кт 6432 (на 6412 — проведенням ПН); актив
          отримує стан «проданий». Ціну вкажіть без ПДВ.
        </p>
      )}
      <TchTable>
        <thead>
          <tr style={{ textTransform: 'none' }}>
            <th style={{ width: 40 }}>#</th>
            <th>{linesTab === 'assets' ? 'Основний засіб' : t('gs.nomenclature')}</th>
            {linesTab === 'assets' && (
              <th style={{ width: 130, textAlign: 'right' }}
                  title="Первісна − знос на момент продажу (з картки; після проведення — снапшот)">
                Залишкова
              </th>
            )}
            {linesTab === 'services' && (
              <th title="Текст у друк акта наданих послуг">
                Зміст послуги
              </th>
            )}
            {linesTab === 'goods' && (
              <th style={{ width: 150 }}
                  title="Склад рядка (порожньо — склад з шапки): відвантаження з різних складів одним документом">
                Склад
              </th>
            )}
            <th className="num" style={{ width: 90 }}>{t('gs.qty')}</th>
            <th className="num" style={{ width: 110 }}
                title={form.vat_flags.prices_include_vat ? 'Ціна рядка вже з ПДВ («сума включає ПДВ»)' : undefined}>
              {form.vat_flags.prices_include_vat ? 'Ціна з ПДВ' : t('gs.price')}
            </th>
            <th className="num" style={{ width: 80 }} title="Знижка рядка, % (дефолт — з договору)">Знижка %</th>
            <th className="num" style={{ width: 130 }}>
              {showVatColumns ? t('gs.amount_without_vat') : 'Сума'}
            </th>
            {showVatColumns && (
              <>
                <th style={{ width: 100 }}>{t('gs.vat')}</th>
                <th className="num" style={{ width: 110 }}>{t('gs.vat_amount')}</th>
                <th className="num" style={{ width: 120 }}>{t('gs.total')}</th>
              </>
            )}
            {isPosted && <th className="num" style={{ width: 120 }}>{t('gs.cost_amount')}</th>}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ it, i }) => (
            <>
            <tr key={i}>
              <td>{it.line_number}</td>
              {it.fixed_asset_id ? (<>
                <td>{faLabel(it.fixed_asset_id)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {it.fa_primary_cost_snapshot != null
                    ? fmt(Number(it.fa_primary_cost_snapshot) - Number(it.fa_accumulated_depreciation_snapshot ?? 0))
                    : fmt(Number(faById.get(it.fixed_asset_id)?.residual_value ?? 0))}
                </td>
              </>) : (
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id ?? ''}
                  disabled={isPosted}
                  kind={linesTab === 'services' ? 'services' : 'goods'}
                  onChange={(nomId) => {
                          setItem(i, {
                            nomenclature_id: nomId,
                            series_id: null,
                            // Auto-fill uom_id з nomenclature.base_uom_id (2026-06-02).
                            uom_id: null,
                            // Етап 2.1: ставка ПДВ з картки (пересчёт vat_amount у setItem).
                            vat_rate_id: showVatColumns
                              ? (vatRateByNomId.get(nomId) ?? it.vat_rate_id)
                              : exemptVatId,
                            tax_purpose_id: taxPurposeByNomId.get(nomId) ?? it.tax_purpose_id,
                          });
                          // П2: знижка з договору для нового товару у рядку.
                          if (contractDiscount && !(Number(it.discount_percent) > 0)) {
                            setItem(i, { discount_percent: contractDiscount });
                          }
                          // П1: автопідстановка ціни за типом цін договору (як в УТП).
                          void priceFor(nomId).then((p) => {
                            if (p != null) setItem(i, { price: p });
                          });
                        }}
                />
              </td>
              )}
              {linesTab === 'services' && (
                <td>
                  <input value={it.service_description ?? ''}
                         onChange={(e) => setItem(i, { service_description: e.target.value })}
                         disabled={isPosted} maxLength={500}
                         placeholder="текст у друк акта" style={{ width: '100%' }} />
                </td>
              )}
              {linesTab === 'goods' && (
                <td>
                  <select value={it.warehouse_id ?? ''}
                          onChange={(e) => setItem(i, { warehouse_id: e.target.value || null })}
                          disabled={isPosted} style={{ width: '100%' }}>
                    <option value="">— за замовчуванням —</option>
                    {warehousesQ.data?.map((w) => (
                      <option key={w.id} value={w.id}>{w.name} — {w.code}</option>
                    ))}
                  </select>
                </td>
              )}
              <td>
                {(() => {
                  const available = it.nomenclature_id
                    ? availableByNomId.get(it.nomenclature_id)
                    : undefined;
                  const reqQty = Number(it.quantity || 0);
                  const overshoot = available !== undefined && reqQty > available;
                  return (
                    <>
                      <input
                        type="number"
                        step="0.001"
                        value={it.quantity}
                        onChange={(e) => setItem(i, { quantity: e.target.value })}
                        disabled={isPosted}
                        style={overshoot ? {
                          borderColor: '#dc2626',
                          background: 'var(--badge-red-bg)',
                        } : undefined}
                        title={overshoot
                          ? `Доступно: ${available}. POST впаде 422.`
                          : (available !== undefined
                              ? `Доступно: ${available}`
                              : undefined)}
                      />
                      {overshoot && (
                        <div style={{
                          fontSize: '0.7rem',
                          color: '#dc2626',
                          marginTop: 2,
                          whiteSpace: 'nowrap',
                        }}>
                          ⚠ доступно {available}
                        </div>
                      )}
                    </>
                  );
                })()}
              </td>
              <td>
                <input type="number" step="0.01" value={it.price}
                       onChange={(e) => setItem(i, { price: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td>
                <input type="number" step="0.01" min="0" max="100" value={it.discount_percent ?? '0'}
                       onChange={(e) => setItem(i, { discount_percent: e.target.value })}
                       disabled={isPosted} />
              </td>
              <td style={{ textAlign: 'right' }}>{fmt(Number(it.amount_without_vat))}</td>
              {showVatColumns && (
                <>
                  <td>
                    <select value={it.vat_rate_id}
                            onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                            disabled={isPosted}>
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {!form.vat_flags.vat_auto && !isPosted ? (
                      <input type="number" step="0.01" min="0" value={it.vat_amount}
                             title="Сума ПДВ вручну (авторозрахунок ПДВ вимкнено)"
                             onChange={(e) => setItem(i, { vat_amount: e.target.value })} />
                    ) : fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Number(it.total_amount))}</td>
                </>
              )}
              {isPosted && (
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmt(Number(it.cost_amount ?? 0))}
                </td>
              )}
              <td>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
            {it.nomenclature_id && useSeriesMap.get(it.nomenclature_id) && (
              <tr key={`ser-${i}`} style={{ background: 'var(--badge-amber-bg)', fontSize: '0.8rem' }}>
                <td></td>
                <td colSpan={2}>
                  <label>
                    <span style={{ color: 'var(--badge-amber-fg)', marginRight: '0.3rem' }}>Серія:</span>
                    <select
                      value={it.series_id ?? ''}
                      onChange={(e) => setItem(i, { series_id: e.target.value || null })}
                      disabled={isPosted}
                      style={{ minWidth: 220 }}
                    >
                      <option value="">— оберіть серію —</option>
                      {(seriesByNom[it.nomenclature_id] ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.code ? ` — ${s.code}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                </td>
                <td colSpan={(showVatColumns ? 8 : 5)}></td>
              </tr>
            )}
            </>
          ))}
        </tbody>
        <tfoot>
          {/* ч.750: «Разом» — вкладка, «Усього» — весь документ. */}
          <tr style={{ fontWeight: 500 }}>
            <td colSpan={6} style={{ textAlign: 'right' }}>Разом:</td>
            <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(tabVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(tabWithoutVat + tabVat)}</td>
              </>
            )}
            {isPosted && (
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(tabCost)}</td>
            )}
            <td></td>
          </tr>
          <tr>
            <td colSpan={6} style={{ textAlign: 'right' }}>{t('gs.total')}:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td style={{ textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            {isPosted && (
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(totalCost)}</td>
            )}
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      {quickNomOpen && (
        <QuickNomenclatureModal
          onClose={() => setQuickNomOpen(false)}
          onCreated={(nom) => {
            setQuickNomOpen(false);
            setForm((f) => ({
              ...f,
              items: [...f.items, {
                ...newRow(f.items.length + 1,
                  showVatColumns
                    ? (nom.vat_rate_id ?? effectiveDefaultVatId)
                    : exemptVatId,
                  nom.tax_purpose_id ?? defaultTaxPurposeId),
                nomenclature_id: nom.id,
                discount_percent: contractDiscount || '0',
                ...(linesTab === 'equipment'
                  ? { account_code: '152', revenue_account_code: '712', cost_account_code: '943' }
                  : {}),
              }],
            }));
            setLinesTab(nom.is_service ? 'services' : linesTab === 'equipment' ? 'equipment' : 'goods');
          }}
        />
      )}
      </>)}
      {/* /viewTab lines */}

      </DocBlock>

      <DocBlock index={5} title="Коментарі та вкладення">
      {/* ч.854: як у рахунку — дві колонки: коментар | вкладення. */}
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.goods_sales" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
      {commWarning && (
        <CommissionerWarningModal check={commWarning} onChoose={onCommissionerChoice} />
      )}
      {basisPickerOpen && (() => {
        const activeDocs = (customerOrdersQ.data?.items ?? [])
          .filter((o) => (o.closing_status ?? 'OPEN') !== 'CLOSED'
                      && o.state !== 'CANCELLED');
        const invoices = activeDocs.filter((o) => o.order_kind === 'PROFORMA_INVOICE');
        const orders = activeDocs.filter((o) => o.order_kind === 'ORDER');
        const pick = (oid: string) => {
          setForm((f) => ({ ...f, customer_order_id: oid }));
          void fillFromOrder(oid);
          setBasisPickerOpen(false);
        };
        const rows = (docs: typeof invoices) => docs.map((o) => (
          <tr key={o.id} onClick={() => pick(o.id)}
              style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-soft)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
            <td>{o.document_number}</td>
            <td>
              {new Date(o.document_date).toLocaleDateString('uk-UA')}
            </td>
            <td style={{ textAlign: 'right' }}>
              {Number(o.total_amount).toLocaleString('uk-UA', { minimumFractionDigits: 2 })}
            </td>
          </tr>
        ));
        return (
          <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'var(--overlay)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => setBasisPickerOpen(false)}>
            <div className="card" style={{ width: 640, maxHeight: '70vh', overflowY: 'auto', margin: 0 }}
                 onClick={(e) => e.stopPropagation()}>
              <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Активні рахунки і замовлення покупця</h3>
                <button style={{ marginLeft: 'auto' }} onClick={() => setBasisPickerOpen(false)}>закрити</button>
              </div>
              {activeDocs.length === 0 && (
                <p style={{ color: 'var(--muted)' }}>
                  Немає відкритих рахунків чи замовлень цього покупця.
                </p>
              )}
              {invoices.length > 0 && (<>
                <div style={{ fontWeight: 600, margin: '0.4rem 0 0.2rem' }}>Рахунки покупцю</div>
                <table className="tch">
                  <tbody>{rows(invoices)}</tbody>
                </table>
              </>)}
              {orders.length > 0 && (<>
                <div style={{ fontWeight: 600, margin: '0.6rem 0 0.2rem' }}>Замовлення покупця</div>
                <table className="tch">
                  <tbody>{rows(orders)}</tbody>
                </table>
              </>)}
              <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: '0.5rem' }}>
                Вибір заповнить табличну частину залишком до відвантаження; при
                проведенні реалізації резерв за рахунком знімається автоматично.
              </p>
            </div>
          </div>
        );
      })()}
      {/* ч.927 (рішення власника): товари (і «Обладнання») підбираються тим самим
          попапом, що й у рахунку покупцю — наявність, баланс, ціни, сортування.
          requireStock: реалізувати можна ЛИШЕ те, що є на складі (рядок без
          залишку не обирається; проведення бекенд блокує, як і раніше). */}
      {pickerOpen && form.organization_id && linesTab !== 'services' && (
        <NomenclatureAvailabilityPicker
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || null}
          multiSelect
          requireStock
          prefsKey="picker:goods-sale"
          onPick={(row, price) => { addRowsFromPicker([row], { price: price ?? null }); setPickerOpen(false); }}
          onPickMany={(rows) => addRowsFromPicker(rows, { price: null })}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {pickerOpen && form.organization_id && linesTab === 'services' && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          defaultWarehouseId={form.warehouse_id || null}
          catalogMode={linesTab === 'services'}
          onlyKind={linesTab === 'services' ? 'services' : 'goods'}
          defaultOnlyWithStock={linesTab !== 'services'}
          multiSelect
          onPick={addRowFromPicker}
          onPickMany={addRowsFromPicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {faPickerOpen && form.organization_id && (
        <FixedAssetPickerModal
          organizationId={form.organization_id}
          excludeIds={form.items.map((it) => it.fixed_asset_id ?? '').filter(Boolean)}
          onPick={addFaFromPicker}
          onClose={() => setFaPickerOpen(false)}
        />
      )}
      {/* ч.948: попапи швидкого створення просто з форми документа. */}
      {quickContractor && (
        <QuickContractorModal
          direction={'IN'}
          onClose={() => setQuickContractor(false)}
          onCreated={(cid) => {
            setQuickContractor(false);
            setForm((f) => ({ ...f, contractor_id: cid, contract_id: '' }));
            qc.invalidateQueries({ queryKey: ['contractors'] });
          }} />
      )}
      {quickContract && form.contractor_id && (
        <QuickContractModal
          orgId={form.organization_id}
          contractorId={form.contractor_id}
          contractorName={contractorNameForQuick}
          direction={'IN'}
          onClose={() => setQuickContract(false)}
          onCreated={(cid) => {
            setQuickContract(false);
            setForm((f) => ({ ...f, contract_id: cid }));
            qc.invalidateQueries({ queryKey: ['contracts', form.organization_id, form.contractor_id] });
          }} />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
